import { computed, type ComputedRef, ref } from 'vue'

import type { DocIdentifier } from '@/types/external/2work/api/common/DocIdentifier'
import type {
  CompanyManagementRequest,
  ManagementDuplicateChoice,
  ManagementSearchHints,
  ManagementSearchState,
  ManagementSuggestion,
  ManagementSuggestionValues,
} from '@/types/managementSuggestionTypes'

import { DOCTYPES, isDoctype } from '@/constants/doctypes'
import { PERMISSIONS } from '@/constants/permissions'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useAuthStore } from '@/stores/auth'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import {
  acceptSuggestion,
  applySaveResults,
  buildApplyPersons,
  chooseDuplicate,
  createManagementSuggestions,
  discardSuggestion,
  reopenSuggestion,
  type SaveResultSummary,
  setSuggestionMatches,
  updateSuggestionValues,
} from '@/utilities/managementSuggestions'
import { translationFunctionDefault } from '@/utilities/translations'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
const useManagementSuggestions = () => {
  const customAPIStore = useFrappeNestCustomAPIStore()
  const suggestions = ref<ManagementSuggestion[]>([])
  const searchState = ref<ManagementSearchState>('idle')
  const saving = ref(false)
  // The request of the shown suggestions: its organization is needed again to check edited names
  const searchRequest = ref<CompanyManagementRequest | null>(null)
  // The company the server searched the management of, shown above the suggestions
  const searchHints = ref<ManagementSearchHints | null>(null)
  // A newer search or a reset makes the answer of an older search obsolete
  let searchNumber = 0

  const search = async (request: CompanyManagementRequest): Promise<void> => {
    searchNumber += 1
    const currentSearchNumber = searchNumber
    suggestions.value = []
    searchRequest.value = request
    searchHints.value = null
    searchState.value = 'loading'

    try {
      const response = await customAPIStore.getCompanyManagementAI(request)
      if (currentSearchNumber !== searchNumber) return

      searchHints.value = response.search_hints ?? null
      suggestions.value = createManagementSuggestions(response.persons)
      searchState.value = 'done'
    } catch (exception) {
      if (currentSearchNumber !== searchNumber) return

      console.error('getCompanyManagementAI Error:', exception)
      searchState.value = 'error'
    }
  }

  const reset = (): void => {
    searchNumber += 1
    suggestions.value = []
    searchRequest.value = null
    searchHints.value = null
    searchState.value = 'idle'
  }

  // Shows the loading state while the company search still runs; the management search follows with its result
  const startPending = (organizationName: string): void => {
    reset()
    searchRequest.value = { organization_name: organizationName.trim() }
    searchState.value = 'loading'
  }

  const accept = (id: number): void => {
    suggestions.value = acceptSuggestion(suggestions.value, id)
  }

  const discard = (id: number): void => {
    suggestions.value = discardSuggestion(suggestions.value, id)
  }

  const reopen = (id: number): void => {
    suggestions.value = reopenSuggestion(suggestions.value, id)
  }

  const updateValues = async (id: number, values: ManagementSuggestionValues): Promise<void> => {
    suggestions.value = updateSuggestionValues(suggestions.value, id, values)
    const suggestion = suggestions.value.find((currentSuggestion) => currentSuggestion.id === id)
    if (!suggestion?.matchesPending) return

    const currentSearchNumber = searchNumber
    try {
      const matches = await customAPIStore.findManagementPersonMatches({
        first_name: suggestion.values.first_name,
        last_name: suggestion.values.last_name,
        organization: searchRequest.value?.organization,
        organization_doctype: searchRequest.value?.organization_doctype,
      })
      if (currentSearchNumber !== searchNumber) return

      suggestions.value = setSuggestionMatches(suggestions.value, id, matches)
    } catch (exception) {
      if (currentSearchNumber !== searchNumber) return

      // Without an answer the person can still be accepted; the server refuses to link a wrong person
      console.error('findManagementPersonMatches Error:', exception)
      suggestions.value = setSuggestionMatches(suggestions.value, id, {
        duplicates: [],
        existing_contact: null,
      })
    }
  }

  const selectDuplicate = (id: number, choice: ManagementDuplicateChoice): void => {
    suggestions.value = chooseDuplicate(suggestions.value, id, choice)
  }

  // Saves all accepted suggestions or only the one with onlyId
  const save = async (
    organization: DocIdentifier,
    onlyId?: number,
  ): Promise<null | SaveResultSummary> => {
    const suggestionsToSave = suggestions.value.filter((suggestion) => {
      return suggestion.status === 'accepted' && (onlyId === undefined || suggestion.id === onlyId)
    })
    if (suggestionsToSave.length === 0) return null

    saving.value = true
    try {
      const response = await customAPIStore.applyManagementSuggestions({
        organization: organization.name,
        organization_doctype: organization.doctype,
        persons: buildApplyPersons(suggestionsToSave),
      })
      const summary = applySaveResults(
        suggestions.value,
        suggestionsToSave.map((suggestion) => suggestion.id),
        response.results,
      )
      suggestions.value = summary.suggestions

      return summary
    } finally {
      saving.value = false
    }
  }

  return {
    accept,
    discard,
    reopen,
    reset,
    save,
    saving,
    search,
    searchHints,
    searchRequest,
    searchState,
    selectDuplicate,
    startPending,
    suggestions,
    updateValues,
  }
}

type ManagementSuggestionsContext = ReturnType<typeof useManagementSuggestions>

const useCanSuggestManagement = (
  organizationDoctype: () => null | string | undefined,
): ComputedRef<boolean> => {
  const authStore = useAuthStore()

  return computed(() => {
    const doctype = organizationDoctype()
    const canWriteOrganization =
      !doctype || (isDoctype(doctype) && authStore.hasPermission(doctype, PERMISSIONS.WRITE))

    return canWriteOrganization && authStore.hasPermission(DOCTYPES.CONTACT, PERMISSIONS.CREATE)
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
const useManagementSaveNotifications = () => {
  const simpleNotificationStore = useSimpleNotificationStore()
  const tfDefault = translationFunctionDefault()

  const notifySaveSummary = (summary: null | SaveResultSummary): void => {
    if (!summary) return

    const details = [
      summary.createdCount > 0
        ? tfDefault('managementSuggestions.savedCreated', { count: summary.createdCount })
        : '',
      summary.linkedCount > 0
        ? tfDefault('managementSuggestions.savedLinked', { count: summary.linkedCount })
        : '',
    ].filter(Boolean)
    if (details.length > 0) {
      simpleNotificationStore.triggerSimpleNotificationSuccess(
        tfDefault('managementSuggestions.saveSuccess', { details: details.join(', ') }),
      )
    }
    if (summary.failedNames.length > 0) {
      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('managementSuggestions.saveFailedPersons', {
          names: summary.failedNames.join(', '),
        }),
        false,
        true,
      )
    }
  }

  const notifySaveError = (exception: unknown): void => {
    console.error('applyManagementSuggestions Error:', exception)
    simpleNotificationStore.triggerSimpleNotificationError(
      tfDefault('managementSuggestions.saveError'),
      false,
      true,
    )
  }

  const notifySearchStillRunning = (): void => {
    simpleNotificationStore.triggerSimpleNotificationInformation(
      tfDefault('managementSuggestions.searchStillRunning'),
      true,
    )
  }

  return { notifySaveError, notifySaveSummary, notifySearchStillRunning }
}

export {
  type ManagementSuggestionsContext,
  useCanSuggestManagement,
  useManagementSaveNotifications,
  useManagementSuggestions,
}
